/**
 * EventFormPage - Create / edit an event with URL-based routing
 * Fetches the full event (including participants) before rendering the form.
 * Routes: /admin/events/new  |  /admin/events/:id
 * @atomic @idempotent
 */
import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import AdminEventForm from '../../components/admin/AdminEventForm'
import AdminHeader from '../../components/admin/AdminHeader'
import { eventsAPI } from '../../services/api/events'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { createLogger } from '../../utils/logger'

const logger = createLogger('EventFormPage')

export default function EventFormPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { execute } = useAsyncOperation()

  const isNew = id === 'new'

  // undefined = still loading; null = new event (no prefill)
  const [event, setEvent] = useState(isNew ? null : undefined)

  useEffect(() => {
    if (isNew) return
    execute(
      () => eventsAPI.adminGetEvent(id),
      {
        onSuccess: (res) => setEvent(res.data),
        onError: (err) => {
          logger.error('Failed to fetch event', err)
          navigate('/admin/events')
        },
      }
    )
  }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

  const loading = !isNew && event === undefined

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader />
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
            </div>
          ) : (
            <AdminEventForm
              event={event}
              onSave={() => navigate('/admin/events')}
              onCancel={() => navigate('/admin/events')}
            />
          )}
        </div>
      </div>
    </div>
  )
}
